import { useEffect, useState } from 'react';
import RemoveNoteModal from './DeleteNoteModal';
import EditNoteModal from './EditNoteModal';

import { QueryKey, useQueryClient } from '@tanstack/react-query';
import {
  useDeleteSubmissionNote,
  useUpdateSubmissionNote
} from 'src/api/commands';
import { useToast } from '@morressier/alembic';
import {
  ISubmissionFile,
  TaggableEntity
} from '@publishing-engine/domain/models';
import { ISubmissionNotesListItem } from '@publishing-engine/domain/views';

type INoteFormPayload = {
  title: string;
  note: string;
  mentions: TaggableEntity[];
  attachments?: ISubmissionFile[];
};
interface Props {
  data: ISubmissionNotesListItem;
  submissionId: string;
  submissionContextId: string;
  organizationId: string;
  fetchNotesQuery?: QueryKey;
  externalEditTrigger?: boolean;
  externalDeleteTrigger?: boolean;
  onParentClose?: () => void;
}

const NoteContextMenu: React.FC<Props> = ({
  data,
  submissionId,
  submissionContextId,
  fetchNotesQuery,
  organizationId,
  externalEditTrigger,
  externalDeleteTrigger,
  onParentClose
}) => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const updateNoteMutation = useUpdateSubmissionNote({
    queryKey: ['submission-notes', { submissionId }],
    submissionId,
    submissionContextId,
    organizationId
  });

  const deleteNoteMutation = useDeleteSubmissionNote({
    queryKey: ['submission-notes', { submissionId }],
    submissionId,
    submissionContextId,
    organizationId
  });

  const onDelete = async () => {
    const response = await deleteNoteMutation.mutateAsync({
      id: data.id
    });
    if (response.isSuccess) {
      setDeleteModalOpen(false);
      setIsDeleting(false);
      // @TODO: When pagination component get fixed, please try to use `setQueryData()` instead of `invalidateQueries()`.
      // ref: https://gitlab.com/morressier/publishing-engine/publishing-engine/-/merge_requests/385#note_2082851187
      fetchNotesQuery &&
        queryClient.invalidateQueries({ queryKey: fetchNotesQuery });
      toast({
        variant: 'success',
        title: 'Note Deleted'
      });
      onParentClose?.();
    } else {
      toast({
        variant: 'error',
        title: response.message
      });
    }
  };
  const onEdit = async (updatedNoteData: INoteFormPayload) => {
    const formattedMentions =
      updatedNoteData.mentions?.filter(
        mention =>
          mention?.id && mention?.name && mention?.email && mention?.type
      ) || [];

    const response = await updateNoteMutation.mutateAsync({
      id: data.id,
      title: updatedNoteData?.title,
      content: updatedNoteData?.note,
      mentions: formattedMentions,
      attachments: updatedNoteData.attachments
    });

    if (response.isSuccess) {
      setEditModalOpen(false);
      fetchNotesQuery &&
        queryClient.invalidateQueries({ queryKey: fetchNotesQuery });
      toast({
        variant: 'success',
        title: 'Note Edits Saved'
      });
      onParentClose?.();
      return true;
    } else {
      let errorMsg = response.message;
      // handing zod error from api response
      if (errorMsg?.errors.length) {
        errorMsg = errorMsg.errors?.[0]?.message;
      }
      toast({
        variant: 'error',
        title: errorMsg
      });
      return false;
    }
  };

  useEffect(() => {
    if (externalEditTrigger) {
      setEditModalOpen(true);
    }
  }, [externalEditTrigger]);

  useEffect(() => {
    if (externalDeleteTrigger) {
      setDeleteModalOpen(true);
    }
  }, [externalDeleteTrigger]);

  return (
    <>
      <EditNoteModal
        isOpen={isEditModalOpen}
        onClose={() => setEditModalOpen(false)}
        onEditSave={onEdit}
        note={data}
        organizationId={organizationId}
        submissionContextId={submissionContextId}
      />
      <RemoveNoteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onRemove={onDelete}
        isLoading={isDeleting}
      />
    </>
  );
};

export default NoteContextMenu;
